import {
    Box, Stack, Typography, Button, Textarea, CircularProgress, Sheet,
} from "@mui/joy";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useParams, useNavigate } from "react-router";

import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ArrowBackRoundedIcon    from '@mui/icons-material/ArrowBackRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import SyncRoundedIcon         from '@mui/icons-material/SyncRounded';
import CancelRoundedIcon       from '@mui/icons-material/CancelRounded';
import HelpOutlineRoundedIcon  from '@mui/icons-material/HelpOutlineRounded';
import CheckCircleRoundedIcon  from '@mui/icons-material/CheckCircleRounded';
import LocationOnRoundedIcon   from '@mui/icons-material/LocationOnRounded';
import EventRoundedIcon        from '@mui/icons-material/EventRounded';

import { LayoutContext } from "../context";
import { APIUnsuccessfulSurveyorCloseEvent } from "../api";

const REASON_OPTIONS = [
    { id: 'transferred', label: 'Перенесли', icon: <SyncRoundedIcon />,       color: '#b07d00', bg: 'rgba(176,125,0,0.08)' },
    { id: 'cancelled',   label: 'Отменили',  icon: <CancelRoundedIcon />,     color: '#c30013', bg: 'rgba(195,0,19,0.08)'  },
    { id: 'other',       label: 'Другое',    icon: <HelpOutlineRoundedIcon />, color: '#572AC3', bg: 'rgba(87,42,195,0.08)' },
];

const slideVariants = {
    enterRight: { x: 40, opacity: 0 }, enterLeft: { x: -40, opacity: 0 },
    center: { x: 0, opacity: 1 }, exitLeft: { x: -40, opacity: 0 }, exitRight: { x: 40, opacity: 0 },
};

export default function SurveyorFailPage() {
    const { bitrixId } = useParams();
    const navigate = useNavigate();
    const { listEvents, tokenEmployee, setListEvents } = useContext(LayoutContext);

    const [step, setStep] = useState(0);
    const [dir, setDir] = useState(1);
    const [reasonType, setReasonType] = useState(null);
    const [comment, setComment] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // Перенос: режим даты и сама дата (только для reasonType === 'transferred')
    const [transferMode, setTransferMode] = useState('undecided'); // 'undecided' | 'date'
    const [transferDate, setTransferDate] = useState('');           // 'YYYY-MM-DDTHH:MM'

    // Событие по bitrixId — как в SurveyorClosePage
    const selectedEvent = useMemo(() => {
        if (!bitrixId) return null;
        return (listEvents || []).find(
            e => e?.bitrix_deal?.id?.toString() === bitrixId.toString()
        ) || null;
    }, [bitrixId, listEvents]);

    const _needDate = reasonType === 'transferred' && transferMode === 'date';
    const canNext = step === 0
        ? Boolean(reasonType)
        : comment.trim().length > 0 && (!_needDate || Boolean(transferDate));

    const goBack = () => navigate(-1);

    const handleSubmit = async () => {
        if (!selectedEvent?.id) { setError('Событие не найдено'); return; }
        if (!comment.trim()) { setError('Введите комментарий'); return; }
        setLoading(true); setError('');
        try {
            const _transferDate = (reasonType === 'transferred' && transferMode === 'date')
                ? transferDate
                : null;
            const data = await APIUnsuccessfulSurveyorCloseEvent(
                selectedEvent.id, reasonType, comment, tokenEmployee, _transferDate,
            );
            if (data.status === 'ok') {
                setListEvents?.(prev => prev.map(e =>
                    e.id === selectedEvent.id ? { ...e, state: 'completed', is_completed: true } : e
                ));
                navigate('/surveyor/calendar', { replace: true });
            } else {
                setError(data.message || 'Ошибка при отправке');
            }
        } catch {
            setError('Ошибка соединения с сервером');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ maxWidth: 560, mx: 'auto', px: 2, py: 3 }}>
            <Button variant="plain" color="neutral" startDecorator={<ArrowBackRoundedIcon />}
                onClick={goBack} sx={{ mb: 1.5 }}>
                Назад
            </Button>

            {/* Шапка */}
            <Box sx={{
                background: 'linear-gradient(135deg, #c30013 0%, #e05060 100%)',
                px: 3, pt: 3, pb: 2.5, borderRadius: '18px 18px 10px 10px',
            }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{
                        width: 40, height: 40, borderRadius: '12px',
                        background: 'rgba(255,255,255,0.2)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', color: 'white',
                    }}>
                        <ErrorOutlineRoundedIcon sx={{ fontSize: 22 }} />
                    </Box>
                    <Box>
                        <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                            Неудачное завершение · шаг {step + 1} из 2
                        </Typography>
                        <Typography level="title-md" sx={{ color: 'white', fontWeight: 800 }}>
                            {step === 0 ? 'Причина' : 'Комментарий'}
                        </Typography>
                    </Box>
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    {[0, 1].map(i => (
                        <motion.div key={i}
                            animate={{ width: i === step ? 28 : 8, background: i <= step ? 'white' : 'rgba(255,255,255,0.3)' }}
                            transition={{ duration: 0.3 }}
                            style={{ height: 8, borderRadius: 99 }} />
                    ))}
                </Stack>
            </Box>

            <Sheet variant="outlined" sx={{ borderRadius: '0 0 18px 18px', borderTop: 'none', p: 2.5 }}>
                {/* Инфо о событии */}
                {selectedEvent ? (
                    <Stack spacing={0.5} sx={{ mb: 2 }}>
                        <Typography level="title-sm" sx={{ fontWeight: 800 }}>
                            Событие #{selectedEvent?.bitrix_deal?.id}
                        </Typography>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <EventRoundedIcon sx={{ fontSize: 15, color: 'text.tertiary' }} />
                            <Typography level="body-xs" color="neutral">{selectedEvent?.date_event || '—'}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <LocationOnRoundedIcon sx={{ fontSize: 15, color: 'text.tertiary' }} />
                            <Typography level="body-xs" color="neutral">{selectedEvent?.bitrix_deal?.full_address || selectedEvent?.bitrix_deal?.address || '—'}</Typography>
                        </Stack>
                    </Stack>
                ) : bitrixId ? (
                    <Stack alignItems="center" spacing={1} sx={{ py: 3 }}>
                        <CircularProgress size="sm" />
                        <Typography level="body-sm" color="neutral">Загружаем событие №{bitrixId}…</Typography>
                    </Stack>
                ) : null}

                <Box sx={{ position: 'relative', minHeight: 200 }}>
                    <AnimatePresence mode="wait" custom={dir}>
                        <motion.div key={step} custom={dir} variants={slideVariants}
                            initial={dir > 0 ? 'enterRight' : 'enterLeft'} animate="center"
                            exit={dir > 0 ? 'exitLeft' : 'exitRight'}
                            transition={{ duration: 0.22, ease: 'easeInOut' }}>
                            {step === 0 ? (
                                <Box sx={{ py: 1 }}>
                                    <Typography level="title-sm" sx={{ mb: 1, fontWeight: 700 }}>
                                        Укажите причину неудачного завершения
                                    </Typography>
                                    <Stack spacing={1.5}>
                                        {REASON_OPTIONS.map(opt => (
                                            <motion.div key={opt.id} whileTap={{ scale: 0.97 }}>
                                                <Box onClick={() => setReasonType(opt.id)}
                                                    sx={{
                                                        display: 'flex', alignItems: 'center', gap: 2, p: 2,
                                                        borderRadius: '14px', cursor: 'pointer',
                                                        border: `2px solid ${reasonType === opt.id ? opt.color : 'rgba(0,0,0,0.08)'}`,
                                                        background: reasonType === opt.id ? opt.bg : 'transparent',
                                                        transition: 'all 0.18s',
                                                        '&:hover': { borderColor: opt.color, background: opt.bg },
                                                    }}>
                                                    <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: opt.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: opt.color, flexShrink: 0 }}>
                                                        {opt.icon}
                                                    </Box>
                                                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{opt.label}</Typography>
                                                    {reasonType === opt.id && (
                                                        <CheckCircleRoundedIcon sx={{ ml: 'auto', color: opt.color, fontSize: 20 }} />
                                                    )}
                                                </Box>
                                            </motion.div>
                                        ))}
                                    </Stack>
                                </Box>
                            ) : (
                                <Box sx={{ py: 1 }}>
                                    <Typography level="title-sm" sx={{ mb: 1, fontWeight: 700 }}>
                                        Опишите ситуацию подробнее
                                    </Typography>
                                    <Typography level="body-xs" sx={{ mb: 2, color: 'text.tertiary' }}>
                                        Комментарий обязателен — он поможет менеджеру разобраться.
                                    </Typography>

                                    {reasonType === 'transferred' && (
                                        <Box sx={{ mb: 2, p: 1.75, borderRadius: '14px', background: 'rgba(176,125,0,0.06)', border: '1px solid rgba(176,125,0,0.2)' }}>
                                            <Typography level="title-sm" sx={{ fontWeight: 700, mb: 1 }}>
                                                Дата переноса
                                            </Typography>
                                            <Stack direction="row" spacing={1} sx={{ mb: transferMode === 'date' ? 1.5 : 0 }}>
                                                <Button
                                                    fullWidth size="sm"
                                                    variant={transferMode === 'undecided' ? 'solid' : 'outlined'}
                                                    color={transferMode === 'undecided' ? 'warning' : 'neutral'}
                                                    onClick={() => setTransferMode('undecided')}
                                                    sx={{ borderRadius: '10px', fontWeight: 700 }}
                                                >
                                                    Неопределённая
                                                </Button>
                                                <Button
                                                    fullWidth size="sm"
                                                    variant={transferMode === 'date' ? 'solid' : 'outlined'}
                                                    color={transferMode === 'date' ? 'warning' : 'neutral'}
                                                    onClick={() => setTransferMode('date')}
                                                    sx={{ borderRadius: '10px', fontWeight: 700 }}
                                                >
                                                    Конкретная дата
                                                </Button>
                                            </Stack>
                                            {transferMode === 'date' && (
                                                <input
                                                    type="datetime-local"
                                                    value={transferDate}
                                                    onChange={e => setTransferDate(e.target.value)}
                                                    style={{
                                                        width: '100%', padding: '10px 12px',
                                                        borderRadius: 10, border: '1.5px solid rgba(176,125,0,0.4)',
                                                        fontSize: '0.9rem', outline: 'none',
                                                    }}
                                                />
                                            )}
                                            <Typography level="body-xs" sx={{ mt: 1, color: 'text.tertiary', lineHeight: 1.5 }}>
                                                {transferMode === 'date'
                                                    ? 'Замер будет отмечен как перенесённый на выбранную дату.'
                                                    : 'Перенос без конкретной даты — менеджер согласует её позже.'}
                                            </Typography>
                                        </Box>
                                    )}

                                    <Textarea value={comment} onChange={e => setComment(e.target.value)}
                                        minRows={5} maxRows={10}
                                        placeholder="Например: клиент не открыл дверь, не взял трубку..."
                                        sx={{
                                            borderRadius: '14px', fontSize: '0.88rem',
                                            border: '1.5px solid rgba(195,0,19,0.2)',
                                            '&:focus-within': { borderColor: '#c30013', boxShadow: '0 0 0 3px rgba(195,0,19,0.1)' },
                                        }} />
                                    {error && (
                                        <Typography level="body-sm" sx={{ color: '#c30013', fontWeight: 600, mt: 1 }}>{error}</Typography>
                                    )}
                                </Box>
                            )}
                        </motion.div>
                    </AnimatePresence>
                </Box>

                {/* Навигация */}
                <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                    {step > 0 ? (
                        <Button variant="outlined" color="neutral" startDecorator={<ArrowBackRoundedIcon />}
                            onClick={() => { setDir(-1); setStep(0); }}
                            sx={{ flex: 0.4, borderRadius: '12px', fontWeight: 700 }}>
                            Назад
                        </Button>
                    ) : (
                        <Button variant="outlined" color="neutral" onClick={goBack}
                            sx={{ flex: 0.4, borderRadius: '12px', fontWeight: 700 }}>
                            Отмена
                        </Button>
                    )}
                    <Button fullWidth variant="solid" disabled={!canNext || loading} loading={loading}
                        onClick={() => { if (step === 0) { setDir(1); setStep(1); } else handleSubmit(); }}
                        endDecorator={step === 0 ? <ArrowForwardRoundedIcon /> : <CheckCircleRoundedIcon />}
                        sx={{
                            flex: 1, borderRadius: '12px', fontWeight: 700, py: 1.3,
                            background: canNext && !loading ? 'linear-gradient(135deg, #c30013 0%, #e05060 100%)' : undefined,
                        }}>
                        {step === 0 ? 'Далее' : 'Завершить событие'}
                    </Button>
                </Box>
            </Sheet>
        </Box>
    );
}